/* eslint-disable react-hooks/exhaustive-deps */
import axios from 'axios'
import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {SelectPageSize} from '../../../../components/SelectPageSize/SelectPageSize'
import {StyledCard} from '../../../../components/StyledCard'
import * as TableUI from '../../../../components/Table/Table'
import {useRowTable} from '../../../../hooks/useRowTable'
import {useAppDispatch, useAppSelector} from '../../../../redux/hooks'
import {
  filterSelector,
  resetFilterLogSAP,
  setFilterLogSAP,
  setIsFetchData,
} from '../../../../redux/slices/filterSlices'
import {fetchLogSAP, headers} from '../../../../services/fetcher'
import {objToParams} from '../../../../utils/objToParams'
import Pagination from '../../component/Pagination'
import {RowFilter} from './RowFilter'
import {RowHead} from './RowHead'
import {StyledButtonGroups} from '../../../../components/StyledButtonGroups'
import {RowData, RowDataProps} from './RowData'
import ModalJsonView from '../components/ModalJsonView'

type Props = {
  className: string
}

const TableLog: React.FC<Props> = ({className}) => {
  const {filterLogSAP} = useAppSelector(filterSelector)
  const dispatch = useAppDispatch()

  const {RowEmptyData, RowErrorFetch, RowLoader} = useRowTable({
    colSpan: 9,
    onReloadTable: () => dispatch(resetFilterLogSAP()),
  })

  const [log, setLog] = useState<RowDataProps['items']>([])
  const [page, setPage] = useState(0)
  const [loading, setLoading] = useState(true)
  const [numberURL, setNumberURL] = useState([])
  const [isError, setIsError] = useState(false)

  const [modalTitle, setModalTitle] = useState('')
  const [isShow, setIsShow] = useState(false)
  const [json, setJson] = useState({})

  const rowNumber = useMemo(() => {
    const per = filterLogSAP?.per_page ?? 5
    return page * per - per
  }, [filterLogSAP, page])

  const paginateNumber = async (url: any) => {
    try {
      setLoading(true)

      const endpoint = `${url}&${objToParams(filterLogSAP)}`
      const token = localStorage.getItem('token') as string
      const res = await axios.get(endpoint, {
        headers: {...headers, Authorization: `Bearer ${token}`},
      })
      const data = res.data.data

      setLog(data.data)
      setPage(data.current_page)
      setNumberURL(data.links)
    } catch (error) {
      setIsError(true)
    } finally {
      setLoading(false)
    }
  }

  const onFetchLogSAP = useCallback(() => {
    setLoading(true)
    setIsError(false)
    fetchLogSAP(filterLogSAP)
      .then((res) => {
        setLog(res.data.data.data)
        setPage(res.data.data.current_page)
        setNumberURL(res.data.data.links)
      })
      .catch(() => setIsError(true))
      .finally(() => {
        setLoading(false)
        dispatch(setIsFetchData(false))
      })
  }, [filterLogSAP])

  const handlePageSizeChange = (value: number | null | undefined) => {
    dispatch(setFilterLogSAP({...filterLogSAP, per_page: value ?? 5}))
  }

  useEffect(() => {
    const getData = setTimeout(onFetchLogSAP, 1000)
    return () => clearTimeout(getData)
  }, [onFetchLogSAP])

  useEffect(() => {
    return () => {
      dispatch(resetFilterLogSAP())
    }
  }, [])

  const showJsonPreview = (title: string, data: any) => {
    setModalTitle(title)
    setIsShow(true)
    try{
      setJson(JSON.parse(data))
    }catch(e){}
  }

  return (
    <StyledCard>
      <StyledButtonGroups
        leftContent={
          <SelectPageSize
            initialValue={filterLogSAP?.per_page ?? 5}
            onChange={(value: any) => handlePageSizeChange(value?.value)}
          />
        }
      />

      <TableUI.TableWrapper>
        <TableUI.Table>
          <TableUI.THead>
            <RowHead />
          </TableUI.THead>

          <TableUI.TBody>
            <RowFilter />

            {loading ? (
              <RowLoader />
            ) : isError ? (
              <RowErrorFetch />
            ) : log.length === 0 ? (
              <RowEmptyData />
            ) : (
              <RowData showPreview={showJsonPreview} items={log} rowNumber={rowNumber} />
            )}
          </TableUI.TBody>
        </TableUI.Table>
      </TableUI.TableWrapper>

      <Pagination paginate={paginateNumber} test={numberURL} />

      <ModalJsonView isShow={isShow} json={json} setShow={setIsShow} title={modalTitle}/>
    </StyledCard>
  )
}

export {TableLog}
